import React, { useState } from "react";
import { FiPlus, FiSave } from "react-icons/fi";
import Button from "components/atoms/Button";
import ActionButton from "components/atoms/ActionButton";
import InputField from "components/atoms/InputField";
import InputSelect from "components/atoms/InputSelect";
import {
  CONDITION_FIELDS,
  OPERATORS,
  OPERATOR_LABELS,
  fieldByValue,
  formatConditionValue,
  isMultiOperator,
  pickValue,
  toOption,
} from "../constants";
import { getConditionErrors } from "../validation";

const EMPTY_CONDITION = { field_code: "", operator: "", value: "" };
const emptyValueFor = (operator) =>
  operator === "BETWEEN" ? ["", ""] : isMultiOperator(operator) ? [] : "";

// Kondisi satu Rule (Condition[] dari RuleDetail.rule_conditions).
const ClinicalConditionStep = ({ conditions, onChange, readOnly, showErrors }) => {
  const [draft, setDraft] = useState(EMPTY_CONDITION);
  const [editingIndex, setEditingIndex] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const field = fieldByValue(draft.field_code);
  const errors = getConditionErrors(draft);
  const errorOf = (key) => submitted && errors[key];
  const operatorOptions = (field ? OPERATORS[field.type] : []).map((op) => ({
    label: `${OPERATOR_LABELS[op]} (${op})`,
    value: op,
  }));

  const reset = () => {
    setDraft(EMPTY_CONDITION);
    setEditingIndex(null);
    setSubmitted(false);
  };

  const save = () => {
    if (Object.keys(errors).length) return setSubmitted(true);
    const value =
      field.type === "number"
        ? Array.isArray(draft.value)
          ? draft.value.map(Number)
          : Number(draft.value)
        : draft.value;
    const item = { ...draft, value };
    onChange(
      editingIndex === null
        ? [...conditions, { ...item, id: `new-${Date.now()}` }]
        : conditions.map((c, i) => (i === editingIndex ? { ...c, ...item } : c))
    );
    reset();
  };

  const startEdit = (c, index) => {
    const value =
      c.operator === "BETWEEN" ? c.value.map(String) : typeof c.value === "number" ? String(c.value) : c.value;
    setDraft({ field_code: c.field_code, operator: c.operator, value });
    setEditingIndex(index);
    setSubmitted(false);
  };

  const renderValueInput = () => {
    const common = { label: "Nilai", isRequired: true, error: errorOf("value"), errorMessage: errors.value };
    if (!field || !draft.operator)
      return <InputField {...common} disabled placeholder="Pilih field & operator dahulu" value="" />;
    if (draft.operator === "BETWEEN")
      return (
        <div className="mps-between">
          {["Dari", "Sampai"].map((placeholder, i) => (
            <InputField
              key={placeholder}
              type="number"
              label={i === 0 ? "Nilai (inklusif)" : " "}
              placeholder={placeholder}
              value={draft.value[i]}
              onChange={(e) =>
                setDraft((prev) => {
                  const value = [...prev.value];
                  value[i] = e.target.value;
                  return { ...prev, value };
                })
              }
              error={errorOf("value") && i === 0}
              errorMessage={errors.value}
            />
          ))}
        </div>
      );
    if (field.type === "number")
      return (
        <InputField
          {...common}
          type="number"
          placeholder="Masukkan angka"
          value={draft.value}
          onChange={(e) => setDraft((prev) => ({ ...prev, value: e.target.value }))}
        />
      );
    if (isMultiOperator(draft.operator))
      return (
        <InputSelect
          {...common}
          isMultipleSelect
          placeholder="Pilih nilai"
          data={field.choices}
          value={field.choices.filter((c) => draft.value.includes(c.value))}
          onChange={(selected) => setDraft((prev) => ({ ...prev, value: (selected || []).map((s) => s.value) }))}
        />
      );
    return (
      <InputSelect
        {...common}
        placeholder="Pilih nilai"
        data={field.choices}
        value={toOption(field.choices, draft.value)}
        onChange={(sel) => setDraft((prev) => ({ ...prev, value: pickValue(sel) }))}
      />
    );
  };

  return (
    <>
      {!readOnly && (
        <div className="mps-subcard">
          {editingIndex !== null && (
            <p className="mps-helper mt-0 mb-3">Mengubah kondisi #{editingIndex + 1}</p>
          )}
          <div className="row">
            <div className="col-md-6 col-lg-3 mps-field">
              <InputSelect
                label="Field kondisi"
                isRequired
                placeholder="Pilih field"
                data={CONDITION_FIELDS}
                value={toOption(CONDITION_FIELDS, draft.field_code)}
                onChange={(sel) => setDraft({ ...EMPTY_CONDITION, field_code: pickValue(sel) })}
                error={errorOf("field_code")}
                errorMessage={errors.field_code}
              />
            </div>
            <div className="col-md-6 col-lg-3 mps-field">
              <InputSelect
                label="Operator"
                isRequired
                placeholder={field ? "Pilih operator" : "Pilih field dahulu"}
                disabled={!field}
                data={operatorOptions}
                value={toOption(operatorOptions, draft.operator)}
                onChange={(sel) => {
                  const operator = pickValue(sel);
                  setDraft((prev) => ({ ...prev, operator, value: emptyValueFor(operator) }));
                }}
                error={errorOf("operator")}
                errorMessage={errors.operator}
              />
            </div>
            <div className="col-lg-6 mps-field">{renderValueInput()}</div>
          </div>
          <div className="mps-actions-start">
            <Button type="button" variant="secondary" size="normal" onClick={save}>
              {editingIndex === null ? <FiPlus size={16} className="mr-2" /> : <FiSave size={16} className="mr-2" />}
              {editingIndex === null ? "Tambah kondisi" : "Simpan perubahan"}
            </Button>
            {editingIndex !== null && (
              <Button type="button" variant="tertiary" size="normal" onClick={reset}>
                Batal
              </Button>
            )}
          </div>
        </div>
      )}

      <h3 className="mps-section-title">
        Kondisi rule ini <span className="mps-muted">({conditions.length})</span>
      </h3>
      <p className="mps-helper mb-3">
        Kondisi pada field yang sama digabung dengan <strong>OR</strong>; field berbeda digabung
        dengan <strong>AND</strong>.
      </p>
      {conditions.length ? (
        <ul className="mps-list">
          {conditions.map((c, i) => (
            <li key={c.id || i} className={`mps-condition-row ${editingIndex === i ? "is-editing" : ""}`}>
              <span className="mps-condition-field">{fieldByValue(c.field_code)?.label}</span>
              <span className="mps-condition-op" title={c.operator}>
                {OPERATOR_LABELS[c.operator]}
              </span>
              <span className="mps-condition-value">{formatConditionValue(c)}</span>
              <code className="mps-code">{c.field_code}</code>
              {!readOnly && (
                <span className="mps-row-actions">
                  <ActionButton btnType="edit" onClick={() => startEdit(c, i)} />
                  <ActionButton
                    btnType="delete"
                    onClick={() => {
                      if (editingIndex === i) reset();
                      onChange(conditions.filter((_, idx) => idx !== i));
                    }}
                  />
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className={`mps-empty ${showErrors ? "is-error" : ""}`}>
          Rule ini belum punya kondisi{readOnly ? "." : ". Tambahkan minimal satu kondisi."}
        </div>
      )}
    </>
  );
};

export default ClinicalConditionStep;
